<script setup lang="ts">
  import { ref, watch, onMounted, computed } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import type { CustomerCase, PatchedCustomerCaseRequest } from '@/types/generated/types.gen'
  import { EntityTypeEnum } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'
  import { CasesService } from '@/types/generated/services.gen'
  import type { GenericObject } from 'vee-validate'
  import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
  import { Switch } from '@/components/ui/switch'
  import { CompanyLegalFormEnum, TitleEnum } from '@/types/generated/types.gen'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import { dateRule } from '@/lib/util/dateRule'
  const props = defineProps<{
    caseData: CustomerCase
    debounceDelay?: number
    throttleInterval?: number
  }>()

  const emit = defineEmits(['update:case-data', 'update:export-opponent-data'])

  const { t } = useTranslation({
    en: {
      opponentInformation: 'Opponent',
      title: 'Title',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      street: 'Street',
      streetNumber: 'House Number',
      zip: 'ZIP Code',
      city: 'City',
      companyLegalForm: 'Legal Form',
      companyName: 'Company Name',
      contactTitle: 'Contact Title',
      contactFirstName: 'Contact First Name',
      contactLastName: 'Contact Last Name',
      isCompany: 'Is Legal Entity (juristische Person)',
      exportOpponentData: 'Include in export',
      invalidEmail: 'Invalid email',
      emailRequired: 'Email is required',
      phoneRequired: 'Phone is required',
      firstNameRequired: 'First Name is required',
      lastNameRequired: 'Last Name is required',
      companyNameRequired: 'Company Name is required',
      legalFormRequired: 'Legal Form is required',
      clerk: 'Clerk',
      assistant: 'Assistant',
      actaportSection: 'Actaport Information',
      clerkRequired: 'Clerk is required',
      assistantRequired: 'Assistant is required',
      dateOfBirth: 'Date of Birth',
      datePlaceholder: 'DD.MM.YYYY',
      titleValidOptions: 'Please select a valid option'
    },
    de: {
      opponentInformation: 'Gegner',
      title: 'Anrede',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefon',
      address: 'Adresse',
      street: 'Straße',
      streetNumber: 'Hausnummer',
      zip: 'Postleitzahl',
      city: 'Stadt',
      companyLegalForm: 'Rechtsform',
      companyName: 'Firma',
      contactTitle: 'Kontakt Anrede',
      contactFirstName: 'Kontakt Vorname',
      contactLastName: 'Kontakt Nachname',
      isCompany: 'Ist juristische Person',
      exportOpponentData: 'In Akte anlegen',
      invalidEmail: 'Ungültige E-Mail-Adresse',
      emailRequired: 'E-Mail ist erforderlich',
      phoneRequired: 'Telefon ist erforderlich',
      firstNameRequired: 'Vorname ist erforderlich',
      lastNameRequired: 'Nachname ist erforderlich',
      companyNameRequired: 'Firmenname ist erforderlich',
      legalFormRequired: 'Rechtsform ist erforderlich',
      clerk: 'Sachbearbeiter',
      assistant: 'Assistent',
      actaportSection: 'Actaport Informationen',
      clerkRequired: 'Sachbearbeiter ist erforderlich',
      assistantRequired: 'Assistent ist erforderlich',
      dateOfBirth: 'Geburtsdatum',
      datePlaceholder: 'TT.MM.JJJJ',
      titleValidOptions: 'Bitte wählen Sie eine gültige Option'
    }
  })

  const caseData = ref(props.caseData)
  const isCompany = ref(caseData.value.data.opponent.entity_type === EntityTypeEnum.LEGAL_PERSON)
  const exportOpponentData = ref(false)

  const companyLegalFormOptions = computed(() =>
    Object.entries(CompanyLegalFormEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  const contactTitleOptions = computed(() =>
    Object.entries(TitleEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  const baseSchema = {
    email: yup.string().email(t('invalidEmail')).notRequired()
  }

  const individualSchema = {
    ...baseSchema,
    first_name: yup.string().notRequired(),
    last_name: yup.string().required(t('lastNameRequired')),
    title: yup
      .string()
      .required(t('title'))
      .oneOf([TitleEnum.HERR, TitleEnum.FRAU, TitleEnum.DIVERS], t('titleValidOptions'))
  }

  const companySchema = {
    ...baseSchema,
    company_name: yup.string().required(t('companyNameRequired')),
    company_legal_form: yup.string().required(t('legalFormRequired'))
  }

  const currentSchema = computed(() => {
    const schema = isCompany.value ? { ...companySchema } : { ...individualSchema }

    if (!exportOpponentData.value) {
      // When exportOpponentData is false, make all fields optional
      return Object.entries(schema).reduce((acc, [key, value]) => {
        acc[key] = value.clone().notRequired()
        return acc
      }, {})
    }

    return schema
  })

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    updateSchema,
    showErrors
  } = useFormWithValidation(props.caseData.data.opponent, currentSchema.value)

  const handleSave = async (updatedData: GenericObject) => {
    console.log('updatedData', updatedData)
    emit('update:case-data', 'opponent', updatedData)
  }

  onMounted(() => {
    exportOpponentData.value = false
  })

  watch(exportOpponentData, (newValue) => {
    emit('update:export-opponent-data', newValue)
  })

  watch([isCompany, exportOpponentData], () => {
    updateSchema(currentSchema.value)
  })

  const showOpponentInfo = ref(true)
  const toggleOpponentInfo = () => {
    showOpponentInfo.value = !showOpponentInfo.value
  }

  const activeTab = ref(
    isCompany.value ? EntityTypeEnum.LEGAL_PERSON : EntityTypeEnum.NATURAL_PERSON
  )
  watch(isCompany, (newValue) => {
    activeTab.value = newValue ? EntityTypeEnum.LEGAL_PERSON : EntityTypeEnum.NATURAL_PERSON
    handleSave({
      entity_type: newValue ? EntityTypeEnum.LEGAL_PERSON : EntityTypeEnum.NATURAL_PERSON
    })
  })

  watch(activeTab, (newValue) => {
    if (isCompany.value !== (newValue === EntityTypeEnum.LEGAL_PERSON)) {
      isCompany.value = newValue === EntityTypeEnum.LEGAL_PERSON
      handleSave({ entity_type: newValue })
    }
  })
  const handleSwitchChange = (value: boolean) => {
    exportOpponentData.value = value
    emit('update:export-opponent-data', value)
    showOpponentInfo.value = value
  }

  defineExpose({
    validate,
    showErrors
  })

  const emailValidation = yup
    .string()
    .email('Invalid email format')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
    .test('email-tld', 'Invalid email domain', (value) => {
      if (!value) return false // Fail if empty (handled by .required())
      const parts = value.split('@')
      return parts.length === 2 && parts[1].includes('.') && parts[1].split('.').pop()!.length >= 2
    })
    .required('Email is required')
</script>

<template>
  <div
    class="h-auto rounded-xl border bg-white p-6 shadow-md transition-all duration-300 ease-in-out"
  >
    <div class="mx-auto max-w-7xl">
      <div>
        <div
          class="flex items-center justify-between"
          :class="exportOpponentData ? 'mb-4' : ''"
        >
          <h2 class="text-xl font-semibold text-gray-900">{{ t('opponentInformation') }}</h2>
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-2">
              <label
                :for="t('exportOpponentData')"
                class="text-sm text-gray-700"
              >
                {{ t('exportOpponentData') }}
              </label>
              <Switch
                :id="t('exportOpponentData')"
                v-model="exportOpponentData"
                :checked="exportOpponentData"
                class="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-gray-200"
                thumb-class=""
                @update:checked="handleSwitchChange"
              />
            </div>
          </div>
        </div>
        <Tabs
          v-if="exportOpponentData && showOpponentInfo"
          v-model="activeTab"
          class="w-full"
        >
          <TabsList class="mb-4 grid w-full grid-cols-2">
            <TabsTrigger :value="EntityTypeEnum.NATURAL_PERSON">Natürliche Person</TabsTrigger>
            <TabsTrigger :value="EntityTypeEnum.LEGAL_PERSON">Juristische Person</TabsTrigger>
          </TabsList>
          <TabsContent :value="EntityTypeEnum.NATURAL_PERSON">
            <AutoSavingForm
              :debounce-delay="debounceDelay"
              :throttle-interval="throttleInterval"
              class="space-y-8"
              @save="handleSave"
            >
              <div class="grid-cols-0 grid gap-x-2 gap-y-1 sm:grid-cols-6">
                <div class="sm:col-span-2">
                  <SelectField
                    v-model="caseData.data.opponent.title"
                    :options="contactTitleOptions"
                    :label="t('title')"
                    name="title"
                    :is-filterable="true"
                    :error="formErrors.value?.title"
                    :rules="individualSchema.title"
                    @update:model-value="(value: any) => handleFieldChange('title', value)"
                    @focus="() => handleFieldTouch('title')"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.first_name"
                    :label="t('firstName')"
                    name="first_name"
                    :error="formErrors.value?.first_name"
                    :rules="yup.string().required()"
                    @update:model-value="(value: any) => handleFieldChange('first_name', value)"
                    @focus="() => handleFieldTouch('first_name')"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.last_name"
                    :label="t('lastName')"
                    name="last_name"
                    :error="formErrors.value?.last_name"
                    :rules="yup.string().required()"
                    @update:model-value="(value) => handleFieldChange('last_name', value)"
                    @focus="() => handleFieldTouch('last_name')"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.email"
                    :label="t('email')"
                    name="email"
                    type="email"
                    :error="formErrors.value?.email"
                    :rules="emailValidation"
                    @update:model-value="(value: any) => handleFieldChange('email', value)"
                    @focus="() => handleFieldTouch('email')"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.phone"
                    :label="t('phone')"
                    name="phone"
                    type="tel"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.date_of_birth"
                    :label="t('dateOfBirth')"
                    name="date_of_birth"
                    :placeholder="t('datePlaceholder')"
                    :rules="dateRule"
                    @update:model-value="(value: any) => handleFieldChange('date_of_birth', value)"
                    @focus="() => handleFieldTouch('date_of_birth')"
                  />
                </div>
                <div class="sm:col-span-4">
                  <InputField
                    v-model="caseData.data.opponent.street"
                    :label="t('street')"
                    name="street"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.street_number"
                    :label="t('streetNumber')"
                    name="streetNumber"
                  />
                </div>
                <div class="sm:col-span-4">
                  <InputField
                    v-model="caseData.data.opponent.city"
                    :label="t('city')"
                    name="city"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.zip"
                    :label="t('zip')"
                    name="zip"
                  />
                </div>
              </div>
            </AutoSavingForm>
          </TabsContent>
          <TabsContent :value="EntityTypeEnum.LEGAL_PERSON">
            <AutoSavingForm
              :debounce-delay="debounceDelay"
              :throttle-interval="throttleInterval"
              class="space-y-8"
              @save="handleSave"
            >
              <div class="grid-cols-0 grid gap-x-2 gap-y-1 sm:grid-cols-6">
                <div class="sm:col-span-3">
                  <SelectField
                    v-model="caseData.data.opponent.company_legal_form"
                    :options="companyLegalFormOptions"
                    :label="t('companyLegalForm')"
                    name="company_legal_form"
                    :is-filterable="true"
                    :rules="yup.string().required()"
                    :error="formErrors.value?.company_legal_form"
                    @update:model-value="
                      (value: any) => handleFieldChange('company_legal_form', value)
                    "
                    @focus="() => handleFieldTouch('company_legal_form')"
                  />
                </div>
                <div class="sm:col-span-3">
                  <InputField
                    v-model="caseData.data.opponent.company_name"
                    :label="t('companyName')"
                    name="company_name"
                    :error="formErrors.value?.company_name"
                    :rules="yup.string().required()"
                    @update:model-value="(value: any) => handleFieldChange('company_name', value)"
                    @focus="() => handleFieldTouch('company_name')"
                  />
                </div>
                <div class="sm:col-span-2">
                  <SelectField
                    v-model="caseData.data.opponent.title"
                    :options="contactTitleOptions"
                    :label="t('contactTitle')"
                    name="title"
                    :is-filterable="true"
                    @focus="() => handleFieldTouch('title')"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.first_name"
                    :label="t('contactFirstName')"
                    name="first_name"
                    @update:model-value="(value: any) => handleFieldChange('first_name', value)"
                    @focus="() => handleFieldTouch('first_name')"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.last_name"
                    :label="t('contactLastName')"
                    name="last_name"
                    @update:model-value="(value: any) => handleFieldChange('last_name', value)"
                    @focus="() => handleFieldTouch('last_name')"
                  />
                </div>
                <div class="sm:col-span-3">
                  <InputField
                    v-model="caseData.data.opponent.email"
                    :label="t('email')"
                    name="email"
                    type="email"
                    :error="formErrors.value?.email"
                    :rules="emailValidation"
                    @update:model-value="(value: any) => handleFieldChange('email', value)"
                    @focus="() => handleFieldTouch('email')"
                  />
                </div>
                <div class="sm:col-span-3">
                  <InputField
                    v-model="caseData.data.opponent.phone"
                    :label="t('phone')"
                    name="phone"
                    type="tel"
                  />
                </div>

                <div class="sm:col-span-4">
                  <InputField
                    v-model="caseData.data.opponent.street"
                    :label="t('street')"
                    name="street"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.street_number"
                    :label="t('streetNumber')"
                    name="street_number"
                  />
                </div>
                <div class="sm:col-span-4">
                  <InputField
                    v-model="caseData.data.opponent.city"
                    :label="t('city')"
                    name="city"
                  />
                </div>
                <div class="sm:col-span-2">
                  <InputField
                    v-model="caseData.data.opponent.zip"
                    :label="t('zip')"
                    name="zip"
                  />
                </div>
              </div>
            </AutoSavingForm>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
</template>
